<template>
  <div class="d-contents">
    <div
      class="offers-table-cell offers-table-cell_versions spacey-8"
      :style="{
        'grid-row': '1 / 2',
      }"
    >
      <OfferVersionDropdown :offer="offer" />
      <Button v-if="isDraft" fill size="small" @click="handleOfferSubmit">
        {{ $t('Submit offer') }}
      </Button>
      <Button
        v-else
        :color="isDraft ? 'primary' : 'neutral'"
        :type="isDraft ? 'filled' : 'ghost'"
        fill
        size="small"
        @click="handleOpenEstimates"
      >
        {{ $t('Open estimates') }}
      </Button>
    </div>
    <div :style="{ 'grid-row': '2 / 3' }" class="offers-table-cell offers-table-cell_positions">
      <Button
        :left-icon="isDraft ? 'pencil' : 'menu'"
        size="small"
        type="borderless"
        @click="openEditDescriptionModal(isSubmitted)"
      >
        {{ isSubmitted || isOldOrArchived ? $t('Open description') : $t('Edit description') }}
      </Button>
    </div>
    <div :style="{ 'grid-row': '3 / 4' }" class="offers-table-cell offers-table-cell_files">
      <OfferFiles :offer="offer" :download-only="offer.status !== 'draft'" />
    </div>
    <div
      v-for="(value, cellsIndex) in tenderOffers.lists"
      :key="cellsIndex"
      class="offers-table-cell offers-table-cell_positions"
      :class="{ 'offers-table-cell_disabled': isOldOrArchived }"
      :style="{ 'grid-row': `${cellsIndex + 4} / ${cellsIndex + 5}` }"
    >
      <Button
        v-if="isDraft && !+cellValue(cellsIndex)"
        class="primary pointer no-underline"
        left-icon="x"
        size="small"
        type="borderless"
        @click="goToLocalEstimate(cellsIndex)"
      >
        {{ $t('Fill in positions') }}
      </Button>
      <div v-else-if="cellValue(cellsIndex)" class="d-contents spacex-4">
        <span class="table-cell-currency">{{ currencySymbols[tenderOffers.currency] }}</span>
        <span>
          {{ addSpaces(cellValue(cellsIndex)) }}
        </span>
      </div>
    </div>
    <div
      class="offers-table-cell offers-table-cell_taxes"
      :style="{ 'grid-row': `${rowsCount + 4} / ${rowsCount + 5}` }"
    >
      <ColumnTaxes
        :is-draft="isDraft"
        :offer="offer"
        :taxes="requiredTaxes"
        :currency="tenderOffers.currency"
        @remove-offer-tax="removeOfferVersionTax"
        @open-tax-modal="openTaxModal"
      />
    </div>
  </div>
</template>

<script>
  import DeleteOfferConfirm from '@/components/incoming-tender/modals/DeleteOfferConfirm';
  import { mapActions, mapState } from 'vuex';
  import router from '@/router';
  import AddTaxWindow from '@/components/incoming-tender/modals/AddTaxWindow';
  import ColumnTaxes from '@/components/incoming-tender/TaxesOfColumn';
  import addSpaces from '@/utils/formatting/add-spaces';
  import OfferVersionDropdown from '@/containers/incoming-tender/incoming-tender-offers-table/OfferVersionDropdown';
  import Button from '@/components/ui/Button';
  import OfferFiles from '@/containers/incoming-tender/incoming-tender-offers-table/OfferFiles';
  import EditDescriptionModal from '@/containers/incoming-tender/incoming-tender-offers-table/EditDescriptionModal';
  import TENDER_OFFER_VERSION_STATUSSES from '@/utils/constants/tender-offer-version-statuses';

  export default {
    name: 'IncomingTenderVersionColumn',
    components: {
      ColumnTaxes,
      OfferVersionDropdown,
      Button,
      OfferFiles,
    },
    props: {
      tenderOffers: {
        type: Object,
        required: true,
      },
      columnIndex: {
        type: Number,
        required: true,
      },
      rowsCount: {
        type: Number,
        required: true,
      },
      offer: {
        type: Object,
        required: true,
      },
    },
    computed: {
      ...mapState('global', ['currencySymbols']),
      ...mapState('incomingTender', ['exportErrorUpdateTrigger', 'incomingTenderInfo']),
      isDraft() {
        return this.offer?.status === TENDER_OFFER_VERSION_STATUSSES.draft;
      },
      isOldOrArchived() {
        return ['old', 'archived'].includes(this.offer.status);
      },
      isSubmitted() {
        return this.offer.status === 'submitted';
      },
      requiredTaxes() {
        return this.incomingTenderInfo?.tender?.taxes || [];
      },
    },
    methods: {
      ...mapActions({
        removeOfferVersionTax: 'incomingTender/removeOfferVersionTax',
        changeSelectedVersion: 'incomingTender/changeSelectedVersion',
        submitVersion: 'incomingTender/submitOfferVersion',
        openModal: 'modal/openModal',
        confirm: 'confirmModal/openModal',
        closeConfirm: 'confirmModal/closeModal',
      }),
      addSpaces,
      goToLocalEstimate(index) {
        this.changeSelectedVersion(this.offer);
        router.push({
          name: 'incoming-tender-list',
          params: {
            incomingTenderListId: this.localEstimateLink(index),
          },
        });
      },
      cellValue(index) {
        return this.tenderOffers.list_sums[`${this.tenderOffers.lists[index].id}_${this.offer.id}`];
      },
      localEstimateLink(index) {
        return this.tenderOffers.lists[index].id;
      },
      openDeleteOfferPopup(id) {
        this.openModal({
          component: DeleteOfferConfirm,
          context: {
            tenderVersionId: id,
          },
        });
      },
      openTaxModal({
        offerTaxId = '',
        taxName = '',
        taxValue = '',
        taxId = null,
        updateTax = false,
      } = {}) {
        this.openModal({
          component: AddTaxWindow,
          context: {
            taxName,
            offerTaxId,
            taxValue,
            taxId,
            offerVersionId: this.offer.id,
            updateTax,
          },
        });
      },
      handleOpenEstimates() {
        this.goToLocalEstimate(0);
      },
      handleOfferSubmit() {
        if (!this.offer.description) {
          this.confirm({
            title: this.$t("You didn't add any description"),
            description: this.$t('Are you sure you want to submit offer without description?'),
            cancelText: this.$t('Cancel'),
            actionText: this.$t('Yes, submit'),
            action: this.confirmOfferSubmit,
          });
        } else {
          this.confirmOfferSubmit();
        }
      },
      async confirmOfferSubmit() {
        await this.closeConfirm();
        this.confirm({
          title: this.$t('Submit offer'),
          description: this.$t(
            'After you submit an offer, the general contractor will see it. They’ll write you in the chat or contact you by email or phone number. You can create multiple offers if you need to.'
          ),
          cancelText: this.$t('Return'),
          actionText: this.$t('Submit offer'),
          action: () => {
            this.submitVersion({
              incomingTenderId: this.$route.params.incomingTenderId,
              versionId: this.offer.id,
            });
          },
        });
      },
      openEditDescriptionModal(readOnly = false) {
        this.openModal({
          component: EditDescriptionModal,
          context: {
            offer: this.offer,
            readOnly,
          },
        });
      },
    },
  };
</script>
